/**
 * CERorganizerSVG — Phase 1, Component 9 of the TeachBud Representation Catalog
 *
 * Renders a CER (Claim-Evidence-Reasoning) organizer per §5.2 Argument & Opinion Writing:
 *   - Three equal-height stacked sections
 *   - Claim: blue header, sentence starter "I claim that…"
 *   - Evidence: green header, starter "According to the text… (paragraph ___)"
 *   - Reasoning: purple header, starter "This proves my claim because…"
 *   - Student mode: ruled writing lines in each section
 *   - Grades 4–8 ELA
 */

import React from 'react';

export interface CERorganizerProps {
  /** When true, sections have ruled writing lines. */
  studentMode?: boolean;
  /** Override pixel width. */
  width?: number;
  /** Grade (0=K). Adjusts line count. */
  grade?: number;
  /** Custom sentence starters. */
  claimStarter?: string;
  evidenceStarter?: string;
  reasoningStarter?: string;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W = 520;
const PAD  = 16;
const BOX_W = VB_W - 2 * PAD;
const HEADER_H = 30;
const SECTION_H = 110;
const GAP = 8;
const SECTIONS = 3;
const VB_H = PAD + SECTIONS * (HEADER_H + SECTION_H + GAP) + PAD;

// Colors per spec §5.2 CER
const CLAIM_FILL     = '#2563EB';  // blue-600
const EVIDENCE_FILL  = '#059669';  // emerald-600
const REASONING_FILL = '#7C3AED';  // violet-600
const CLAIM_LIGHT    = '#EFF6FF';
const EVIDENCE_LIGHT = '#ECFDF5';
const REASONING_LIGHT= '#F5F3FF';
const STROKE_COLOR   = '#E5E7EB';
const LINE_COLOR     = '#E5E7EB';
const TEXT_COLOR     = '#1E3A5F';
const STARTER_COLOR  = '#6B7280';

interface SectionConfig {
  label: string;
  headerFill: string;
  bodyFill: string;
  starter: string;
  linesCount: number;
}

export function CERorganizerSVG({
  studentMode = true,
  width,
  grade = 5,
  claimStarter = 'I claim that…',
  evidenceStarter = 'According to the text… (paragraph ___ )',
  reasoningStarter = 'This proves my claim because…',
  className = '',
}: CERorganizerProps) {
  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;
  const HEADER_FS = 13;
  const STARTER_FS = grade <= 4 ? 9.5 : 9;
  const lineCount = grade <= 4 ? 3 : 4;
  const lineSpacing = Math.floor((SECTION_H - 28) / lineCount);

  const sections: SectionConfig[] = [
    { label: 'Claim', headerFill: CLAIM_FILL, bodyFill: CLAIM_LIGHT, starter: claimStarter, linesCount: lineCount },
    { label: 'Evidence', headerFill: EVIDENCE_FILL, bodyFill: EVIDENCE_LIGHT, starter: evidenceStarter, linesCount: lineCount },
    { label: 'Reasoning', headerFill: REASONING_FILL, bodyFill: REASONING_LIGHT, starter: reasoningStarter, linesCount: lineCount },
  ];

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label="CER organizer: Claim, Evidence, Reasoning"
    >
      <title>CER Organizer</title>

      {sections.map(({ label, headerFill, bodyFill, starter, linesCount }, si) => {
        const sectionY = PAD + si * (HEADER_H + SECTION_H + GAP);
        const bodyY = sectionY + HEADER_H;

        return (
          <g key={si}>
            {/* Body background */}
            <rect
              x={PAD} y={bodyY}
              width={BOX_W} height={SECTION_H}
              fill={bodyFill}
              stroke={STROKE_COLOR}
              strokeWidth={1}
            />

            {/* Header */}
            <rect
              x={PAD} y={sectionY}
              width={BOX_W} height={HEADER_H}
              rx={si === 0 ? 6 : 0} ry={si === 0 ? 6 : 0}
              fill={headerFill}
            />
            {/* Bottom clip of rounded header */}
            {si === 0 && (
              <rect x={PAD} y={sectionY + HEADER_H - 6} width={BOX_W} height={7} fill={headerFill} />
            )}

            {/* Section label */}
            <text
              x={PAD + 12} y={sectionY + HEADER_H / 2 + 5}
              fontSize={HEADER_FS}
              fontFamily="Nunito, sans-serif"
              fontWeight={800}
              fill="#FFFFFF"
              letterSpacing={0.5}
            >
              {label.toUpperCase()}
            </text>

            {/* Sentence starter */}
            <text
              x={PAD + 10} y={bodyY + 18}
              fontSize={STARTER_FS}
              fontFamily="Inter, sans-serif"
              fontWeight={500}
              fill={STARTER_COLOR}
              fontStyle="italic"
            >
              {starter}
            </text>

            {/* Ruled writing lines */}
            {studentMode && Array.from({ length: linesCount }).map((_, li) => {
              const ly = bodyY + 28 + li * lineSpacing + lineSpacing / 2;
              if (ly > bodyY + SECTION_H - 6) return null;
              return (
                <line
                  key={li}
                  x1={PAD + 10} y1={ly}
                  x2={PAD + BOX_W - 10} y2={ly}
                  stroke={LINE_COLOR} strokeWidth={1}
                />
              );
            })}

            {/* Bottom border of last section rounded */}
            {si === SECTIONS - 1 && (
              <rect
                x={PAD} y={bodyY}
                width={BOX_W} height={SECTION_H}
                rx={6}
                fill="none"
                stroke={STROKE_COLOR}
                strokeWidth={1.5}
              />
            )}
          </g>
        );
      })}

      {/* Outer rounded border over all sections */}
      <rect
        x={PAD} y={PAD}
        width={BOX_W} height={VB_H - 2 * PAD}
        rx={6}
        fill="none"
        stroke="#D1D5DB"
        strokeWidth={1.5}
      />
    </svg>
  );
}

export function parseCERorganizerHint(_hint: string): CERorganizerProps {
  return { studentMode: true };
}
